import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE } from '../lib/config';
import { useUserStore } from './userStore';
import { useBrowserStore } from './browserStore';

// =============================================================================
// Types
// =============================================================================

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  plan?: string;
  subscriptionStatus?: string | null;
  trialEnd?: string | null;
  hasCompletedOnboarding?: boolean;
}

interface SetAuthParams {
  token: string;
  user: AuthUser;
  hasZoomConnection?: boolean;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  hasZoomConnection: boolean;
  refreshing: boolean;
  lastEmail: string | null;

  // Actions
  setAuth: (params: SetAuthParams) => void;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  setZoomConnected: (connected: boolean) => void;
  setLastEmail: (email: string) => void;
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      hasZoomConnection: false,
      refreshing: false,
      lastEmail: null,

      setAuth: ({ token, user, hasZoomConnection }) => {
        set({
          token,
          user,
          ...(hasZoomConnection !== undefined ? { hasZoomConnection } : {}),
        });
      },

      logout: async () => {
        const { token } = get();
        if (token) {
          try {
            await fetch(`${API_BASE}/auth/logout`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tenant-ID': '',
              },
            });
          } catch {
            // best-effort — clear state regardless
          }
        }
        set({
          token: null,
          user: null,
          hasZoomConnection: false,
          refreshing: false,
        });
        useUserStore.getState().logout();
        window.location.href = '/';
      },

      checkSession: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await fetch(`${API_BASE}/auth/me`, {
            credentials: 'include',
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Tenant-ID': '',
            },
          });

          if (!response.ok) {
            set({ token: null, user: null, hasZoomConnection: false });
            return;
          }

          const data = await response.json();
          set({
            user: {
              ...data.user,
              plan: data.plan,
              subscriptionStatus: data.subscriptionStatus,
              trialEnd: data.trialEnd,
            },
            hasZoomConnection: data.zoom?.connected ?? data.hasZoomConnection ?? false,
          });

          if (data.browser) {
            useBrowserStore.getState().setZoomSignedIn(data.browser.zoomSignedIn ?? false);
          }
        } catch {
          // Network error — don't clear session on transient errors
        }
      },

      refreshAccessToken: async () => {
        const { refreshing } = get();
        if (refreshing) return false;

        set({ refreshing: true });
        try {
          const response = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
          });

          if (!response.ok) {
            set({ token: null, user: null, hasZoomConnection: false, refreshing: false });
            return false;
          }

          const data = await response.json();
          set({ token: data.token, refreshing: false });

          // Hydrate user profile in background
          get().checkSession();
          return true;
        } catch {
          // Network error — don't clear state on transient errors
          set({ refreshing: false });
          return false;
        }
      },

      setZoomConnected: (connected: boolean) => {
        set({ hasZoomConnection: connected });
      },

      setLastEmail: (email: string) => {
        set({ lastEmail: email });
      },
    }),
    {
      name: 'zac-auth',
      partialize: (state) => ({
        user: state.user,
        lastEmail: state.lastEmail,
      }),
    }
  )
);

// =============================================================================
// Helper: get current token for API client
// =============================================================================

export function getToken(): string | null {
  return useAuthStore.getState().token;
}
